import { checkCustomerExists, sendSignInOtp, verifySignInOtp } from "@/lib/api";
import { trackBuyAgainWidgetInteract } from "@/lib/tracking";
import type { WidgetOpenSource } from "@/lib/widgetEvents";
import { Loader2, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { Button } from "./ui/button";
import { Sheet, SheetClose, SheetContent } from "./ui/sheet";

interface SubscriptionSignInStateProps {
  onSignInSuccess?: () => void;
  trackingShop?: string | null;
  openSource?: WidgetOpenSource | null;
  /**
   * Trigger style. "button" (default) renders the pill CTA used in the
   * unified welcome-offer empty state. "link" renders the stacked
   * "Already a customer? / Access Now" treatment from the per-tab empty
   * state, centered with the action styled as a blue text link.
   */
  variant?: "button" | "link";
}

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60;
const UNKNOWN_EMAIL_MSG =
  "We couldn't find an account with that email. Try the email you ordered with.";

export function SubscriptionSignInState({
  onSignInSuccess,
  trackingShop,
  openSource,
  variant = "button",
}: SubscriptionSignInStateProps) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [step, setStep] = useState<"email" | "otp">("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" && window.innerWidth >= 768,
  );
  useEffect(() => {
    const fn = () => setIsDesktop(window.innerWidth >= 768);
    fn();
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  const startCooldown = useCallback(() => {
    setResendCooldown(RESEND_COOLDOWN);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current!);
          cooldownRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleSendOtp = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    setEmail(trimmed);
    setLoading(true);
    setError(null);
    try {
      // Turn an unknown address away here rather than after the code round
      // trip: signing in without a customer record behind it lands on an empty
      // product list with no way to correct the email.
      const exists = await checkCustomerExists(trimmed);
      if (!exists) {
        if (trackingShop) {
          trackBuyAgainWidgetInteract(
            "widget_login_email_unknown",
            trackingShop,
            { open_source: openSource ?? undefined },
          );
        }
        setError(UNKNOWN_EMAIL_MSG);
        return;
      }
      await sendSignInOtp(trimmed);
      if (trackingShop) {
        trackBuyAgainWidgetInteract("widget_login_otp_sent", trackingShop, {
          open_source: openSource ?? undefined,
        });
      }
      setStep("otp");
      startCooldown();
      setTimeout(() => otpRefs.current[0]?.focus(), 50);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to send code. Try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (code: string) => {
    setLoading(true);
    setError(null);
    try {
      await verifySignInOtp(email.trim(), code);
      if (trackingShop) {
        trackBuyAgainWidgetInteract("widget_login_success", trackingShop, {
          open_source: openSource ?? undefined,
        });
      }
      setSheetOpen(false);
      onSignInSuccess?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Invalid or expired code.");
      setOtp(Array(OTP_LENGTH).fill(""));
      setTimeout(() => otpRefs.current[0]?.focus(), 50);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setError(null);
    try {
      await sendSignInOtp(email.trim());
      startCooldown();
    } catch {
      setError("Failed to resend code.");
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const digit = value.slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);

    if (digit && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }

    if (next.every((d) => d !== "")) {
      handleVerifyOtp(next.join(""));
    }
  };

  const handleOtpKeyDown = (index: number, e: KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: ClipboardEvent) => {
    e.preventDefault();
    const pasted = (e.clipboardData?.getData("text") ?? "")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill("");
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setOtp(next);
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    otpRefs.current[focusIdx]?.focus();
    if (next.every((d) => d !== "")) {
      handleVerifyOtp(next.join(""));
    }
  };

  const handleOpenSheet = () => {
    setStep("email");
    setEmail("");
    setOtp(Array(OTP_LENGTH).fill(""));
    setError(null);
    setSheetOpen(true);
    setTimeout(() => emailRef.current?.focus(), 100);
  };

  return (
    <>
      {variant === "link" ? (
        <div
          className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-1"
          style={{ color: "var(--so-text, #523D33)" }}
        >
          <span className="tw:text-base">Already a customer?</span>
          <button
            type="button"
            onClick={handleOpenSheet}
            className="tw:bg-transparent tw:border-none tw:p-0 tw:cursor-pointer tw:text-base tw:text-center"
            style={{ color: "#0396C1" }}
          >
            Access Now
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleOpenSheet}
          className="tw:self-start tw:bg-transparent tw:border tw:border-gray-300 tw:rounded-md tw:py-2 tw:px-8 tw:cursor-pointer tw:text-base tw:font-semibold tw:text-center hover:tw:bg-gray-50 tw:transition-colors"
          style={{ color: "var(--so-text, #523D33)" }}
        >
          Access Now
        </button>
      )}

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent
          side={isDesktop ? "right" : "bottom"}
          className="tw:max-h-svh tw:data-[side=bottom]:max-h-[90dvh] tw:flex tw:flex-col tw:gap-0 tw:rounded-t-2xl md:tw:rounded-t-none md:tw:max-w-sm md:tw:h-full md:tw:max-h-full tw:overflow-hidden tw:z-60 tw:data-[side=bottom]:z-60 tw:data-[side=right]:z-60"
          onOpenChange={setSheetOpen}
          showCloseButton={false}
        >
          {/* Header */}
          <div className="tw:flex tw:items-center tw:justify-between tw:px-4 tw:pt-3 tw:pb-2">
            <span
              className="tw:font-semibold tw:text-base"
              style={{ color: "var(--so-text, #523D33)" }}
            >
              {"\u26A1\uFE0F Hi there!"}
            </span>
            <SheetClose
              data-slot="sheet-close"
              render={
                <Button
                  variant="ghost"
                  className="tw:p-0 tw:m-0"
                  size="icon-sm"
                />
              }
            >
              <X />
            </SheetClose>
          </div>

          {/* Body */}
          <div className="tw:flex-1 tw:min-h-109 tw:overflow-y-auto tw:px-5 tw:py-4 tw:flex tw:flex-col">
            {/* Benefits list */}
            <h2
              className="tw:text-lg tw:font-semibold tw:m-0 tw:mb-6 tw:text-center"
              style={{ color: "var(--so-text, #523D33)" }}
            >
              Get Instant Access
            </h2>
            {step === "email" ? (
              <form
                className="tw:flex tw:flex-col tw:gap-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSendOtp();
                }}
              >
                <input
                  ref={emailRef}
                  type="email"
                  placeholder="Enter email"
                  value={email}
                  onInput={(e) =>
                    setEmail((e.target as HTMLInputElement).value)
                  }
                  className="tw:w-full tw:px-3 tw:py-2.5 tw:text-base tw:rounded-md tw:border tw:border-gray-200 tw:outline-none focus:tw:border-[#D4808A] tw:transition-colors"
                  style={{ color: "var(--so-text, #523D33)" }}
                  disabled={loading}
                />
                <button
                  type="submit"
                  disabled={loading || !email.trim()}
                  className="tw:w-full tw:py-2.5 tw:px-4 tw:text-sm tw:font-semibold tw:rounded-md tw:border-none tw:cursor-pointer tw:transition-opacity disabled:tw:opacity-50 disabled:tw:cursor-not-allowed"
                  style={{ backgroundColor: "#FAC8CA", color: "#523D33" }}
                >
                  {loading ? (
                    <Loader2
                      className="tw:size-4 tw:animate-spin tw:mx-auto"
                      style={{ color: "#523D33" }}
                    />
                  ) : (
                    "Continue"
                  )}
                </button>
              </form>
            ) : (
              <div className="tw:flex tw:flex-col tw:items-center">
                <p
                  className="tw:text-xs tw:mb-3"
                  style={{ color: "var(--so-text, #523D33)", opacity: 0.7 }}
                >
                  We sent a code to{" "}
                  <span className="tw:font-semibold">{email}</span>
                </p>
                <div
                  className="tw:flex tw:justify-center tw:gap-1.5 tw:mb-2"
                  onPaste={handleOtpPaste}
                >
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => {
                        otpRefs.current[i] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onInput={(e) =>
                        handleOtpChange(i, (e.target as HTMLInputElement).value)
                      }
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      disabled={loading}
                      className="tw:w-9 tw:h-10 tw:text-center tw:text-base tw:font-semibold tw:rounded-lg tw:border tw:border-gray-200 tw:outline-none focus:tw:border-[#D4808A] tw:transition-colors"
                      style={{ color: "var(--so-text, #523D33)" }}
                    />
                  ))}
                </div>
                {loading && (
                  <Loader2
                    className="tw:size-4 tw:animate-spin tw:mx-auto tw:mb-2"
                    style={{ color: "#523D33" }}
                  />
                )}
                <div className="tw:flex tw:items-center tw:justify-center tw:gap-3 tw:mt-2">
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={resendCooldown > 0}
                    className="tw:bg-transparent tw:border-none tw:p-0 tw:text-xs tw:cursor-pointer disabled:tw:opacity-40 disabled:tw:cursor-not-allowed"
                    style={{ color: "#2563EB" }}
                  >
                    {resendCooldown > 0
                      ? `Resend in ${resendCooldown}s`
                      : "Resend code"}
                  </button>
                  <span
                    className="tw:text-xs"
                    style={{ color: "var(--so-text, #523D33)", opacity: 0.3 }}
                  >
                    |
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setStep("email");
                      setOtp(Array(OTP_LENGTH).fill(""));
                      setError(null);
                    }}
                    className="tw:bg-transparent tw:border-none tw:p-0 tw:text-xs tw:cursor-pointer"
                    style={{ color: "var(--so-text, #523D33)", opacity: 0.6 }}
                  >
                    Change email
                  </button>
                </div>
              </div>
            )}

            {error && (
              <p className="tw:text-xs tw:mt-2 tw:text-red-500 tw:text-center">
                {error}
              </p>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
